import { WhiteBox, WhiteGrid } from "@foodpilot/foods";
import { Grid2, Stack, useMediaQuery, useTheme } from "@mui/material";
import { SummaryBlock } from "./SummaryBlock";
import { Diagnostic } from "./Diagnostic";
import { SectorTarget } from "./SectoralTarget/SectorTarget";
import { Strategy } from "./Strategy";
import {
    CompanyOverviewOneScore,
    ScoreOverview,
} from "src/api/client-api/company/overview/useOrganizationOverviewOneSsq";
import { useSearchParams } from "react-router";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useTranslation } from "react-i18next";
import { ScoreService } from "src/services/ScoreService";
import { ValidatedState } from "../ValidatedTag";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { round } from "lodash";
import { useEffect, useRef } from "react";
import { getSubscoreOfDisplayModeFromScoreOverview } from "src/utils/scores.ts";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";

type OneScoreOverviewProps = {
    overview: CompanyOverviewOneScore;
    isSingleScore?: boolean;
};

export const OneScoreOverview = (props: OneScoreOverviewProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();

    const { isFeatureActivated } = useApplicationParameters();
    const { scoresWithVariations } = useEntityScores();
    const { overview, isSingleScore = false } = props;

    // At init of the page, it's originally set to this;
    const [params, setParams] = useSearchParams();

    const scoreId = overview.score.id;
    const scoreTabParamKey = `score-${scoreId}-tab`;
    const openedTabParam = params.get(scoreTabParamKey);
    const parsedOpenedTab = openedTabParam === null ? null : Number(openedTabParam);
    let openedTab = parsedOpenedTab !== null && Number.isNaN(parsedOpenedTab) ? null : parsedOpenedTab;

    const score = scoresWithVariations.find((item) => item.scoreId === scoreId && item.variationId === null);

    // Refs for scrolling to content sections on mobile
    const diagnosticRef = useRef<HTMLDivElement>(null);
    const strategyRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const shouldScrollRef = useRef<boolean>(false);

    // Smooth scroll to section on mobile when tab changes via card click
    useEffect(() => {
        if (!isMobile || !overview || !shouldScrollRef.current || openedTab === null) return;

        const scrollToSection = (element: HTMLDivElement | null) => {
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

        // Add a small delay to ensure the content is rendered
        const timer = setTimeout(() => {
            if (openedTab === 0) {
                scrollToSection(diagnosticRef.current);
            } else if (openedTab === 1) {
                scrollToSection(strategyRef.current);
            } else if (openedTab === 2) {
                scrollToSection(targetRef.current);
            }
            // Reset the flag after scrolling
            shouldScrollRef.current = false;
        }, 100);

        return () => clearTimeout(timer);
    }, [openedTab, isMobile, overview]);

    const apCompletions = overview.actionPlan !== false ? (overview.actionPlan?.completion ?? null) : null;
    const diagnosticTitle = t("company.dashboard.diagnostic_title", `{{score}}`, { score: overview.score.title });
    const actionPlanTitle =
        overview.actionPlan === null ?
            t("company.dashboard.stategy.no_action_plan", "Upcoming strategy")
        :   t("company.dashboard.strategy_title", `My strategy for {{endCampaign}}`, {
                endCampaign: overview.actionPlan !== false ? overview.actionPlan?.campaigns.end.year : "",
            });
    const part = overview.actionPlan !== false ? (overview.actionPlan?.completion.engaged.byType.completed ?? 0) : 0;
    const total = overview.actionPlan !== false ? (overview.actionPlan?.completion.engaged.total ?? 1) : 1;
    const completionActionPlan = (part / total) * 100;

    const isDecrease = score?.improvement === "decrease";

    const totalTarget = overview.actionPlan !== false ? (overview.actionPlan?.campaigns.end.value ?? 0) : null;
    const partTarget = overview.target !== false ? overview.target?.value : null;
    const completionTarget =
        isDecrease ?
            partTarget !== null && partTarget !== undefined && totalTarget ?
                round((partTarget / totalTarget) * 100, 3)
            :   undefined
        : totalTarget !== null && totalTarget !== undefined && partTarget ? round((totalTarget / partTarget) * 100, 3)
        : undefined;

    const isRegulatory = overview.score.displayMode === "regulation";
    const _diagnosticScore = overview.ssq?.post?.scores?.score.value;

    const _gradeSubscore = getSubscoreOfDisplayModeFromScoreOverview(overview.ssq?.post?.scores as any, "grade");

    const specificityRateScoreDefinition = overview.score.children.find(
        (subscore: ScoreOverview) => subscore.type === "specificity-rate",
    );
    const specificityRate =
        specificityRateScoreDefinition !== undefined ?
            OlapScoresService.normalizeNumericalScore(
                overview.ssq?.post?.scores?.subscores?.[specificityRateScoreDefinition.id]?.score.value,
            )
        :   undefined;

    const diagnosticScore = ScoreService.extractScoreValue(_diagnosticScore, overview.score.displayMode);
    const diagnosticSubscore =
        _gradeSubscore ?
            ScoreService.extractScoreValue(_gradeSubscore.score.value as any, overview.score.displayMode)
        :   undefined;

    const diagnosticUnit = isRegulatory ? "" : overview.score.unit;

    const shouldShowActionPlan =
        overview.actionPlan !== false && isRegulatory === false && isFeatureActivated("FS-actionPlan");

    // const shouldShowTarget = overview.target !== null && isRegulatory === false;
    const shouldShowTarget = overview.target !== false;

    const diagnosticState: ValidatedState = overview.ssq?.post?.isLocked === true ? "validated" : null;
    // const diagnosticState: ValidatedState = false;

    let numberOfEnabledTabs = 1 + [shouldShowActionPlan, shouldShowTarget].filter((tab) => tab).length; // +1 because the first tab is always enabled
    numberOfEnabledTabs = numberOfEnabledTabs === 0 ? 1 : numberOfEnabledTabs;

    const GRID_COLUMNS_NUMBER = 12;
    const MAXIMUM_TABS_NUMBER = 3;
    const columnsWidthEnabledSpan = (GRID_COLUMNS_NUMBER / MAXIMUM_TABS_NUMBER) * numberOfEnabledTabs;

    const handleTabToggle = (tabIndex: number) => {
        const isSameTab = openedTab === tabIndex;
        const nextOpenedTab = isSameTab ? null : tabIndex;

        if (isMobile && nextOpenedTab !== null) {
            shouldScrollRef.current = true;
        } else {
            shouldScrollRef.current = false;
        }

        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);

                if (isSameTab) {
                    next.delete(scoreTabParamKey);
                } else {
                    next.set(scoreTabParamKey, String(tabIndex));
                }

                return next;
            },
            { replace: true, preventScrollReset: true },
        );
    };

    return (
        <WhiteBox
            sx={{
                padding: theme.spacing(2),
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(2),
            }}
        >
            <Grid2 container columns={GRID_COLUMNS_NUMBER}>
                <WhiteGrid
                    container
                    size={{
                        xs: GRID_COLUMNS_NUMBER,
                        lg: columnsWidthEnabledSpan,
                    }}
                    spacing={theme.spacing(2)}
                    border={0}
                    sx={{
                        boxShadow: "none",
                        backgroundColor: "transparent",
                    }}
                >
                    <Grid2 size={{ xs: GRID_COLUMNS_NUMBER, lg: GRID_COLUMNS_NUMBER / numberOfEnabledTabs }}>
                        <Stack
                            role="button"
                            sx={{ height: "100%", cursor: "pointer" }}
                            onClick={() => handleTabToggle(0)}
                        >
                            <SummaryBlock
                                type="questionnaire"
                                datavizColorIndex={4}
                                icon={"questionnaireAnswer"}
                                title={diagnosticTitle}
                                score={OlapScoresService.normalizeNumericalScore(diagnosticScore)}
                                subScore={OlapScoresService.normalizeNumericalScore(diagnosticSubscore)}
                                unit={diagnosticUnit}
                                completionPercent={overview.ssq?.post?.completion}
                                tag={diagnosticState}
                                completion={`${overview.ssq?.post?.completion ?? 0}%`}
                                completionInfo={t("company.dashboard.percent_subtitle", "of data completed")}
                                isSelected={openedTab === 0}
                                specificityRate={overview.ssq?.post?.isLocked ? specificityRate : undefined}
                                hideChevron={isSingleScore && numberOfEnabledTabs === 1}
                            />
                        </Stack>
                    </Grid2>
                    {shouldShowActionPlan && (
                        <Grid2 size={{ xs: GRID_COLUMNS_NUMBER, lg: GRID_COLUMNS_NUMBER / numberOfEnabledTabs }}>
                            <Stack
                                role="button"
                                sx={{ height: "100%", cursor: "pointer" }}
                                onClick={() => handleTabToggle(1)}
                            >
                                <SummaryBlock
                                    type="action_plan"
                                    datavizColorIndex={12}
                                    icon={"trajectory"}
                                    title={actionPlanTitle}
                                    score={
                                        overview.actionPlan !== false ?
                                            (overview.actionPlan?.campaigns.end.value ?? null)
                                        :   null
                                    }
                                    completionPercent={completionActionPlan}
                                    unit={overview.score.unit}
                                    tag={null}
                                    completion={`${apCompletions?.engaged?.byType?.completed ?? 0}/${apCompletions?.engaged?.total ?? 0}`}
                                    completionInfo={t("company.dashboard.ap_subtitle", "actions completed")}
                                    isSelected={openedTab === 1}
                                />
                            </Stack>
                        </Grid2>
                    )}
                    {shouldShowTarget && (
                        <Grid2 size={{ xs: GRID_COLUMNS_NUMBER, lg: GRID_COLUMNS_NUMBER / numberOfEnabledTabs }}>
                            <Stack
                                role="button"
                                sx={{ height: "100%", cursor: "pointer" }}
                                onClick={() => handleTabToggle(2)}
                            >
                                <SummaryBlock
                                    type="target"
                                    datavizColorIndex={11}
                                    icon={"target"}
                                    title={overview.target !== false ? (overview.target?.label ?? "") : ""}
                                    score={overview.target !== false ? (overview.target?.value ?? null) : null}
                                    completion={completionTarget !== undefined ? completionTarget + "%" : undefined}
                                    completionPercent={completionTarget ?? 0}
                                    completionInfo={t("company.dashboard.of_target_achieved", "of target achieved")}
                                    unit={overview.score.unit}
                                    tag={null}
                                    isSelected={openedTab === 2}
                                />
                            </Stack>
                        </Grid2>
                    )}
                </WhiteGrid>
            </Grid2>

            {openedTab === 0 && (
                <div ref={diagnosticRef}>
                    <Diagnostic
                        diagnosticTitle={diagnosticTitle}
                        diagnostic={overview.ssq}
                        scoreDetail={score}
                        score={overview.score}
                        scoreId={scoreId.toString()}
                        diagnosticSubtitle={
                            isRegulatory ?
                                t(
                                    "company.dashboard.diagnostic_regulatory_subtitle",
                                    "Exigences règlementaires qui s'appliquent à votre entreprise d'après vos réponses au questionnaire.",
                                )
                            :   undefined
                        }
                        validatedState={diagnosticState}
                    />
                </div>
            )}
            {openedTab === 1 && (
                <div ref={strategyRef}>
                    <Strategy
                        scoreDetail={score}
                        actionPlanTitle={actionPlanTitle}
                        actionPlan={overview.actionPlan}
                        diagnostic={overview.ssq}
                        target={overview.target}
                        unit={overview.score.unit}
                    />
                </div>
            )}
            {openedTab === 2 && overview.target !== false && overview.target !== null && (
                <div ref={targetRef}>
                    <SectorTarget target={overview.target} />
                </div>
            )}
        </WhiteBox>
    );
};
